import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Card } from "../components/ui/card";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { ArrowLeft, Upload, FileText, Download, Trash2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { EmployeeForm } from "./Employees";

const DOC_TYPES = ["id", "diploma", "contract", "src", "adr", "passport", "license", "psychotechnical", "health", "other"];

export default function EmployeeDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const [emp, setEmp] = useState(null);
  const [tab, setTab] = useState("info");
  const [editForm, setEditForm] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [assets, setAssets] = useState([]);
  const [discipline, setDiscipline] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/employees/${id}`);
    setEmp(data);
    setEditForm(data);
    const [d, c, l, p, tr, perf, a, disc] = await Promise.all([
      api.get("/documents", { params: { employee_id: id } }),
      api.get("/contracts", { params: { employee_id: id } }),
      api.get("/leaves", { params: { employee_id: id } }),
      api.get("/payroll", { params: { employee_id: id } }),
      api.get("/trainings", { params: { employee_id: id } }),
      api.get("/performance", { params: { employee_id: id } }),
      api.get("/assets", { params: { employee_id: id } }),
      api.get("/discipline", { params: { employee_id: id } }),
    ]);
    setDocuments(d.data);
    setContracts(c.data);
    setLeaves(l.data);
    setPayroll(p.data);
    setTrainings(tr.data);
    setPerformance(perf.data);
    setAssets(a.data);
    setDiscipline(disc.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!emp) return <div className="p-10 text-center text-muted-foreground">Loading...</div>;

  const initials = `${emp.first_name?.[0] || ""}${emp.last_name?.[0] || ""}`.toUpperCase();

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = { ...editForm };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
      await api.put(`/employees/${id}`, payload);
      toast.success(t("saved"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    }
    setSaving(false);
  };

  return (
    <div className="space-y-5" data-testid="emp-detail-page">
      <Link to="/employees" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="btn-back-emp">
        <ArrowLeft className="w-4 h-4 mr-1" /> {t("back")}
      </Link>

      {/* Header */}
      <Card className="p-6 border-border/60">
        <div className="flex flex-col md:flex-row gap-4 md:items-center">
          <Avatar className="w-20 h-20">
            <AvatarFallback className="bg-primary/15 text-primary text-xl font-bold">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2 text-xs font-mono-brand uppercase tracking-wider text-muted-foreground">
              <span>{emp.employee_no}</span><span>·</span><span>{t(emp.employee_type)}</span>
            </div>
            <h1 className="font-display text-2xl font-extrabold">{emp.first_name} {emp.last_name}</h1>
            <div className="text-sm text-muted-foreground">{emp.title} · {emp.department}</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge variant={emp.status === "active" ? "default" : "secondary"}>{t(emp.status)}</Badge>
            <div className="text-xs text-muted-foreground">{emp.email}</div>
            <div className="text-xs text-muted-foreground">{emp.phone}</div>
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger data-testid="tab-info" value="info">{t("personalInfo")}</TabsTrigger>
          <TabsTrigger data-testid="tab-documents" value="documents">{t("documents")}</TabsTrigger>
          <TabsTrigger data-testid="tab-contracts" value="contracts">{t("contracts")}</TabsTrigger>
          <TabsTrigger data-testid="tab-payroll" value="payroll">{t("payroll")}</TabsTrigger>
          <TabsTrigger data-testid="tab-leaves" value="leaves">{t("leaves")}</TabsTrigger>
          <TabsTrigger data-testid="tab-trainings" value="trainings">{t("trainings")}</TabsTrigger>
          <TabsTrigger data-testid="tab-performance" value="performance">{t("performance")}</TabsTrigger>
          <TabsTrigger data-testid="tab-assets" value="assets">{t("assets")}</TabsTrigger>
          <TabsTrigger data-testid="tab-discipline" value="discipline">{t("discipline")}</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-5">
          <Card className="p-6 border-border/60">
            {editForm && <EmployeeForm form={editForm} setForm={setEditForm} />}
            <div className="pt-5 mt-5 border-t border-border/60 flex justify-end">
              <Button data-testid="btn-save-edit" onClick={saveEdit} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("save")}
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="mt-5">
          <DocumentsPanel employeeId={id} docs={documents} onChange={load} />
        </TabsContent>
        <TabsContent value="contracts" className="mt-5">
          <ContractsPanel employeeId={id} rows={contracts} onChange={load} />
        </TabsContent>
        <TabsContent value="payroll" className="mt-5">
          <PayrollPanel employeeId={id} rows={payroll} onChange={load} />
        </TabsContent>
        <TabsContent value="leaves" className="mt-5">
          <LeavesPanel employeeId={id} rows={leaves} onChange={load} />
        </TabsContent>
        <TabsContent value="trainings" className="mt-5">
          <TrainingsPanel employeeId={id} rows={trainings} onChange={load} />
        </TabsContent>
        <TabsContent value="performance" className="mt-5">
          <PerformancePanel employeeId={id} rows={performance} onChange={load} />
        </TabsContent>
        <TabsContent value="assets" className="mt-5">
          <AssetsPanel employeeId={id} rows={assets} onChange={load} />
        </TabsContent>
        <TabsContent value="discipline" className="mt-5">
          <DisciplinePanel employeeId={id} rows={discipline} onChange={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DocumentsPanel({ employeeId, docs, onChange }) {
  const { t } = useI18n();
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("other");
  const [docName, setDocName] = useState("");
  const [expiry, setExpiry] = useState("");
  const [uploading, setUploading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(null);

  const upload = async () => {
    if (!file) return toast.error("No file");
    setUploading(true);
    const fd = new FormData();
    fd.append("employee_id", employeeId);
    fd.append("doc_type", docType);
    fd.append("doc_name", docName || file.name);
    if (expiry) fd.append("expiry_date", expiry);
    fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("saved"));
      setFile(null); setDocName(""); setExpiry("");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    }
    setUploading(false);
  };

  const runOcr = async (did) => {
    setOcrLoading(did);
    try {
      const { data } = await api.post("/ai/document-ocr", { document_id: did });
      toast.success("OCR OK");
      alert(JSON.stringify(data, null, 2));
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    setOcrLoading(null);
  };

  const downloadDoc = async (did) => {
    const token = localStorage.getItem("logihr_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${did}/download?auth=${token}`;
    window.open(url, "_blank");
  };

  const remove = async (did) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/documents/${did}`);
    onChange();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="p-5 border-border/60 lg:col-span-1 h-fit">
        <h3 className="font-display font-bold mb-3">{t("uploadDoc")}</h3>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">{t("docType")}</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="field-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map(d => <SelectItem key={d} value={d}>{t(d === "id" ? "idDoc" : d) || d.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("docName")}</Label>
            <Input data-testid="field-doc-name" value={docName} onChange={e => setDocName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("expiryDate")}</Label>
            <Input data-testid="field-doc-expiry" type="date" value={expiry} onChange={e => setExpiry(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("uploadDocLabel")}</Label>
            <Input data-testid="field-doc-file" type="file" onChange={e => setFile(e.target.files[0])} />
          </div>
          <Button data-testid="btn-upload-doc" onClick={upload} disabled={uploading || !file} className="w-full">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" /> {t("uploadDoc")}</>}
          </Button>
        </div>
      </Card>

      <Card className="p-5 border-border/60 lg:col-span-2">
        <h3 className="font-display font-bold mb-3">{t("documents")}</h3>
        {docs.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{t("noData")}</div>
        ) : (
          <div className="divide-y divide-border/60">
            {docs.map((d, i) => (
              <div key={d.id} data-testid={`doc-row-${i}`} className="py-3 flex items-center gap-3">
                <FileText className="w-4 h-4 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{d.doc_name}</div>
                  <div className="text-xs text-muted-foreground font-mono-brand uppercase tracking-wider">
                    {d.doc_type} · {d.original_filename} {d.expiry_date ? `· ${t("expiryDate")}: ${d.expiry_date}` : ""}
                  </div>
                </div>
                <Button data-testid={`btn-ocr-${i}`} variant="ghost" size="sm" onClick={() => runOcr(d.id)} disabled={ocrLoading === d.id}>
                  {ocrLoading === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                </Button>
                <Button data-testid={`btn-download-${i}`} variant="ghost" size="sm" onClick={() => downloadDoc(d.id)}>
                  <Download className="w-3 h-3" />
                </Button>
                <Button data-testid={`btn-delete-doc-${i}`} variant="ghost" size="sm" onClick={() => remove(d.id)}>
                  <Trash2 className="w-3 h-3 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function MiniForm({ fields, onSubmit, testPrefix }) {
  const { t } = useI18n();
  const [state, setState] = useState({});
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
      {fields.map(f => (
        <div key={f.k} className="space-y-1">
          <Label className="text-xs">{f.label}</Label>
          {f.type === "select" ? (
            <Select value={state[f.k] || ""} onValueChange={(v) => setState(s => ({ ...s, [f.k]: v }))}>
              <SelectTrigger data-testid={`${testPrefix}-${f.k}`}><SelectValue /></SelectTrigger>
              <SelectContent>{f.options.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
          ) : f.type === "textarea" ? (
            <Textarea data-testid={`${testPrefix}-${f.k}`} value={state[f.k] || ""} onChange={e => setState(s => ({ ...s, [f.k]: e.target.value }))} />
          ) : (
            <Input data-testid={`${testPrefix}-${f.k}`} type={f.type || "text"} value={state[f.k] || ""} onChange={e => setState(s => ({ ...s, [f.k]: e.target.value }))} />
          )}
        </div>
      ))}
      <Button data-testid={`${testPrefix}-submit`} onClick={async () => { await onSubmit(state); setState({}); }}>{t("add")}</Button>
    </div>
  );
}

function ContractsPanel({ employeeId, rows, onChange }) {
  const { t } = useI18n();
  const [analyzing, setAnalyzing] = useState(null);
  const [result, setResult] = useState(null);
  const submit = async (s) => {
    try {
      await api.post("/contracts", {
        employee_id: employeeId,
        contract_type: s.contract_type || "permanent",
        start_date: s.start_date,
        end_date: s.end_date || null,
        gross_salary: parseFloat(s.gross_salary) || 0,
        content: s.content || "",
      });
      toast.success(t("saved")); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const analyze = async (c) => {
    setAnalyzing(c.id);
    try {
      const { data } = await api.post("/ai/contract-analysis", { text: c.content || `Contract ${c.contract_type} from ${c.start_date}, gross ${c.gross_salary}` });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    setAnalyzing(null);
  };
  return (
    <div className="space-y-4">
      <Card className="p-5 border-border/60">
        <h3 className="font-display font-bold mb-3">{t("newContract")}</h3>
        <MiniForm
          testPrefix="contract"
          fields={[
            { k: "contract_type", label: t("category"), type: "select", options: [
              { v: "permanent", l: "Permanent" }, { v: "fixed-term", l: "Fixed-Term" }, { v: "temporary", l: "Temporary" }
            ]},
            { k: "start_date", label: t("startDate"), type: "date" },
            { k: "end_date", label: t("endDate"), type: "date" },
            { k: "gross_salary", label: t("grossSalary"), type: "number" },
            { k: "content", label: t("contract"), type: "textarea" },
          ]}
          onSubmit={submit}
        />
      </Card>
      <Card className="p-5 border-border/60">
        {rows.length === 0 ? <div className="text-sm text-muted-foreground text-center py-6">{t("noData")}</div> : (
          <div className="divide-y divide-border/60">
            {rows.map((c, i) => (
              <div key={c.id} className="py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-sm">{c.contract_type} · {c.start_date}{c.end_date ? ` → ${c.end_date}` : ""}</div>
                  <div className="text-xs text-muted-foreground font-mono-brand">{t("grossSalary")}: {c.gross_salary} {c.currency}</div>
                </div>
                <Button data-testid={`btn-analyze-${i}`} size="sm" variant="outline" onClick={() => analyze(c)} disabled={analyzing === c.id}>
                  {analyzing === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Sparkles className="w-3 h-3 mr-1" /> {t("aiContractAnalyzer")}</>}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
      {result && (
        <Card className="p-5 border-border/60">
          <h3 className="font-display font-bold mb-2">{t("summary")}</h3>
          <p className="text-sm whitespace-pre-wrap">{result.summary}</p>
          {result.risks?.length > 0 && (
            <>
              <h4 className="font-semibold mt-4 mb-2">{t("risks")}</h4>
              <ul className="space-y-1 text-sm">
                {result.risks.map((r, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Badge variant={r.severity === "high" ? "destructive" : "secondary"}>{r.severity}</Badge>
                    <span>{r.clause}: {r.note}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      )}
    </div>
  );
}

function PayrollPanel({ employeeId, rows, onChange }) {
  const { t } = useI18n();
  const submit = async (s) => {
    try {
      const gross = parseFloat(s.gross_salary) || 0;
      const bonus = parseFloat(s.bonus) || 0;
      const deductions = parseFloat(s.deductions) || 0;
      const sgk = gross * 0.14;
      const tax = gross * 0.15;
      const net = gross + bonus - deductions - sgk - tax;
      await api.post("/payroll", {
        employee_id: employeeId, period: s.period,
        gross_salary: gross, worked_days: parseFloat(s.worked_days) || 30,
        overtime_hours: 0, overtime_amount: 0, bonus,
        deductions, sgk_deduction: sgk, tax_deduction: tax, net_salary: net,
      });
      toast.success(t("saved")); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  return (
    <div className="space-y-4">
      <Card className="p-5 border-border/60">
        <h3 className="font-display font-bold mb-3">{t("newPayroll")}</h3>
        <MiniForm
          testPrefix="payroll"
          fields={[
            { k: "period", label: t("period"), type: "month" },
            { k: "gross_salary", label: t("grossSalary"), type: "number" },
            { k: "worked_days", label: t("workedDays"), type: "number" },
            { k: "bonus", label: t("bonus"), type: "number" },
            { k: "deductions", label: t("deduction"), type: "number" },
          ]}
          onSubmit={submit}
        />
      </Card>
      <Card className="p-5 border-border/60">
        {rows.length === 0 ? <div className="text-sm text-muted-foreground text-center py-6">{t("noData")}</div> : (
          <div className="divide-y divide-border/60">
            {rows.map((r, i) => (
              <div key={r.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{r.period}</div>
                  <div className="text-xs text-muted-foreground font-mono-brand">
                    {t("netSalary")}: {r.net_salary?.toFixed(2)} {r.currency} · {t("grossSalary")}: {r.gross_salary}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={r.paid ? "default" : "secondary"}>{r.paid ? t("paid") : t("pending")}</Badge>
                  {!r.paid && (
                    <Button data-testid={`btn-paid-${i}`} size="sm" variant="outline" onClick={async () => { await api.post(`/payroll/${r.id}/pay`); onChange(); }}>
                      {t("markPaid")}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function LeavesPanel({ employeeId, rows, onChange }) {
  const { t } = useI18n();
  const submit = async (s) => {
    try {
      const days = (new Date(s.end_date) - new Date(s.start_date)) / 86400000 + 1;
      await api.post("/leaves", {
        employee_id: employeeId, leave_type: s.leave_type || "annual",
        start_date: s.start_date, end_date: s.end_date, days: Math.max(1, days),
        reason: s.reason || null,
      });
      toast.success(t("saved")); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  return (
    <div className="space-y-4">
      <Card className="p-5 border-border/60">
        <h3 className="font-display font-bold mb-3">{t("newLeave")}</h3>
        <MiniForm
          testPrefix="leave"
          fields={[
            { k: "leave_type", label: t("leaveType"), type: "select", options: [
              { v: "annual", l: t("annual") }, { v: "sick", l: t("sick") }, { v: "unpaid", l: t("unpaid") },
              { v: "marriage", l: t("marriage") }, { v: "bereavement", l: t("bereavement") }
            ]},
            { k: "start_date", label: t("startDate"), type: "date" },
            { k: "end_date", label: t("endDate"), type: "date" },
            { k: "reason", label: t("reason") },
          ]}
          onSubmit={submit}
        />
      </Card>
      <Card className="p-5 border-border/60">
        {rows.length === 0 ? <div className="text-sm text-muted-foreground text-center py-6">{t("noData")}</div> : (
          <div className="divide-y divide-border/60">
            {rows.map((r, i) => (
              <div key={r.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{t(r.leave_type)} · {r.start_date} → {r.end_date}</div>
                  <div className="text-xs text-muted-foreground font-mono-brand">{r.days} {t("days")} · {r.reason || "—"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={r.status === "approved" ? "default" : r.status === "rejected" ? "destructive" : "secondary"}>{t(r.status)}</Badge>
                  {r.status === "pending" && (
                    <>
                      <Button data-testid={`btn-approve-${i}`} size="sm" variant="outline" onClick={async () => { await api.post(`/leaves/${r.id}/approve`); onChange(); }}>{t("approve")}</Button>
                      <Button data-testid={`btn-reject-${i}`} size="sm" variant="outline" onClick={async () => { await api.post(`/leaves/${r.id}/reject`); onChange(); }}>{t("reject")}</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function TrainingsPanel({ employeeId, rows, onChange }) {
  const { t } = useI18n();
  const submit = async (s) => {
    try {
      await api.post("/trainings", {
        employee_id: employeeId, title: s.title, category: s.category || "other",
        provider: s.provider || null, start_date: s.start_date, end_date: s.end_date || null,
        certificate_no: s.certificate_no || null, expiry: s.expiry || null,
      });
      toast.success(t("saved")); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  return (
    <div className="space-y-4">
      <Card className="p-5 border-border/60">
        <h3 className="font-display font-bold mb-3">{t("newTraining")}</h3>
        <MiniForm
          testPrefix="training"
          fields={[
            { k: "title", label: t("name") },
            { k: "category", label: t("category"), type: "select", options: [
              { v: "safety", l: "Safety" }, { v: "adr", l: "ADR" }, { v: "first-aid", l: "First Aid" },
              { v: "compliance", l: "Compliance" }, { v: "other", l: t("other") }
            ]},
            { k: "provider", label: t("provider") },
            { k: "start_date", label: t("startDate"), type: "date" },
            { k: "expiry", label: t("expiryDate"), type: "date" },
          ]}
          onSubmit={submit}
        />
      </Card>
      <Card className="p-5 border-border/60">
        {rows.length === 0 ? <div className="text-sm text-muted-foreground text-center py-6">{t("noData")}</div> : (
          <div className="divide-y divide-border/60">
            {rows.map((r, i) => (
              <div key={r.id} className="py-3">
                <div className="font-medium text-sm">{r.title}</div>
                <div className="text-xs text-muted-foreground font-mono-brand">
                  {r.category} · {r.provider || "—"} · {r.start_date}{r.expiry ? ` · ${t("expiryDate")}: ${r.expiry}` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function PerformancePanel({ employeeId, rows, onChange }) {
  const { t } = useI18n();
  const submit = async (s) => {
    try {
      await api.post("/performance", {
        employee_id: employeeId, period: s.period, reviewer: s.reviewer,
        overall_score: parseFloat(s.overall_score) || 0,
        safety_score: parseFloat(s.safety_score) || 0,
        delivery_ontime_score: parseFloat(s.delivery_ontime_score) || 0,
        fuel_efficiency_score: parseFloat(s.fuel_efficiency_score) || 0,
        comments: s.comments || null,
      });
      toast.success(t("saved")); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  return (
    <div className="space-y-4">
      <Card className="p-5 border-border/60">
        <h3 className="font-display font-bold mb-3">{t("newPerformance")}</h3>
        <MiniForm
          testPrefix="perf"
          fields={[
            { k: "period", label: t("period") },
            { k: "reviewer", label: t("reviewer") },
            { k: "overall_score", label: t("overallScore"), type: "number" },
            { k: "safety_score", label: t("safetyScore"), type: "number" },
            { k: "delivery_ontime_score", label: t("deliveryScore"), type: "number" },
            { k: "fuel_efficiency_score", label: t("fuelScore"), type: "number" },
            { k: "comments", label: t("comments"), type: "textarea" },
          ]}
          onSubmit={submit}
        />
      </Card>
      <Card className="p-5 border-border/60">
        {rows.length === 0 ? <div className="text-sm text-muted-foreground text-center py-6">{t("noData")}</div> : (
          <div className="divide-y divide-border/60">
            {rows.map((r, i) => (
              <div key={r.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{r.period} · {r.reviewer}</div>
                  <div className="text-xs text-muted-foreground font-mono-brand">
                    O:{r.overall_score} · S:{r.safety_score} · D:{r.delivery_ontime_score} · F:{r.fuel_efficiency_score}
                  </div>
                </div>
                <Badge>{r.overall_score}/5</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function AssetsPanel({ employeeId, rows, onChange }) {
  const { t } = useI18n();
  const submit = async (s) => {
    try {
      await api.post("/assets", {
        employee_id: employeeId, asset_type: s.asset_type || "other",
        asset_name: s.asset_name, serial_or_plate: s.serial_or_plate || null,
        assigned_date: s.assigned_date, notes: s.notes || null,
      });
      toast.success(t("saved")); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  return (
    <div className="space-y-4">
      <Card className="p-5 border-border/60">
        <h3 className="font-display font-bold mb-3">{t("newAsset")}</h3>
        <MiniForm
          testPrefix="asset"
          fields={[
            { k: "asset_type", label: t("assetType"), type: "select", options: [
              { v: "vehicle", l: t("vehicle") }, { v: "phone", l: t("phone") }, { v: "fuelcard", l: t("fuelcard") },
              { v: "tablet", l: t("tablet") }, { v: "ppe", l: t("ppe") }, { v: "other", l: t("other") }
            ]},
            { k: "asset_name", label: t("assetName") },
            { k: "serial_or_plate", label: t("serialPlate") },
            { k: "assigned_date", label: t("assignedDate"), type: "date" },
          ]}
          onSubmit={submit}
        />
      </Card>
      <Card className="p-5 border-border/60">
        {rows.length === 0 ? <div className="text-sm text-muted-foreground text-center py-6">{t("noData")}</div> : (
          <div className="divide-y divide-border/60">
            {rows.map((r, i) => (
              <div key={r.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{r.asset_name} · {t(r.asset_type)}</div>
                  <div className="text-xs text-muted-foreground font-mono-brand">{r.serial_or_plate || "—"} · {r.assigned_date}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={r.status === "assigned" ? "default" : "secondary"}>{r.status}</Badge>
                  {r.status === "assigned" && (
                    <Button data-testid={`btn-return-asset-${i}`} size="sm" variant="outline" onClick={async () => { await api.post(`/assets/${r.id}/return`); onChange(); }}>
                      {t("returnAsset")}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function DisciplinePanel({ employeeId, rows, onChange }) {
  const { t } = useI18n();
  const submit = async (s) => {
    try {
      await api.post("/discipline", {
        employee_id: employeeId, incident_date: s.incident_date,
        category: s.category || "warning", reason: s.reason,
        description: s.description || null, action_taken: s.action_taken || null,
      });
      toast.success(t("saved")); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  return (
    <div className="space-y-4">
      <Card className="p-5 border-border/60">
        <h3 className="font-display font-bold mb-3">{t("newDiscipline")}</h3>
        <MiniForm
          testPrefix="disc"
          fields={[
            { k: "incident_date", label: t("incidentDate"), type: "date" },
            { k: "category", label: t("category"), type: "select", options: [
              { v: "warning", l: "Warning" }, { v: "reprimand", l: "Reprimand" }, { v: "suspension", l: "Suspension" }
            ]},
            { k: "reason", label: t("reason") },
            { k: "action_taken", label: t("actionTaken") },
            { k: "description", label: t("comments"), type: "textarea" },
          ]}
          onSubmit={submit}
        />
      </Card>
      <Card className="p-5 border-border/60">
        {rows.length === 0 ? <div className="text-sm text-muted-foreground text-center py-6">{t("noData")}</div> : (
          <div className="divide-y divide-border/60">
            {rows.map((r, i) => (
              <div key={r.id} className="py-3">
                <div className="flex items-center gap-2">
                  <Badge variant="destructive">{r.category}</Badge>
                  <span className="font-medium text-sm">{r.reason}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 font-mono-brand">{r.incident_date} · {r.action_taken || "—"}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
